import { useState } from "react";
import { Plus } from "lucide-react";
import {
  useClientBranches,
  useCreateClientBranch,
  useClientSubBranches,
  useCreateClientSubBranch,
} from "@/features/tenders/hooks/useTenders";

interface Props {
  clientId: string | null;
  selectedBranch: string | null;
  selectedSubBranch: string | null;
  onBranchChange: (branchId: string | null) => void;
  onSubBranchChange: (subBranchId: string | null) => void;
  disabled?: boolean;
  branchError?: string;
  subBranchError?: string;
}

export function ClientBranchSelector({
  clientId,
  selectedBranch,
  selectedSubBranch,
  onBranchChange,
  onSubBranchChange,
  disabled,
  branchError,
  subBranchError,
}: Props) {
  const { data: branches = [] } = useClientBranches(clientId ?? undefined);
  const { data: subBranches = [] } = useClientSubBranches(selectedBranch ?? undefined);
  const createBranch = useCreateClientBranch();
  const createSubBranch = useCreateClientSubBranch();

  const [showNewBranch, setShowNewBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [newBranchCity, setNewBranchCity] = useState("");

  const [showNewSubBranch, setShowNewSubBranch] = useState(false);
  const [newSubBranchName, setNewSubBranchName] = useState("");
  const [newSubBranchCity, setNewSubBranchCity] = useState("");

  if (!clientId) return null;

  const handleBranchChange = (value: string) => {
    onBranchChange(value || null);
    onSubBranchChange(null);
    setShowNewSubBranch(false);
  };

  const handleAddBranch = async () => {
    if (!newBranchName.trim() || !clientId) return;
    const branch = await createBranch.mutateAsync({
      client: clientId,
      name: newBranchName.trim(),
      city: newBranchCity.trim() || undefined,
    });
    onBranchChange(branch.id);
    onSubBranchChange(null);
    setNewBranchName("");
    setNewBranchCity("");
    setShowNewBranch(false);
  };

  const handleAddSubBranch = async () => {
    if (!newSubBranchName.trim() || !selectedBranch) return;
    const sub = await createSubBranch.mutateAsync({
      branch: selectedBranch,
      name: newSubBranchName.trim(),
      city: newSubBranchCity.trim() || undefined,
    });
    onSubBranchChange(sub.id);
    setNewSubBranchName("");
    setNewSubBranchCity("");
    setShowNewSubBranch(false);
  };

  const inputCls = (err?: string) =>
    `rounded-md border px-3 py-2 text-sm ${err ? "border-red-400" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`;

  return (
    <div className="flex flex-col gap-3">
      {/* Oddział */}
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
          Oddział klienta <span className="text-gray-400 text-xs">(opcjonalnie)</span>
        </label>
        <div className="flex gap-2">
          <select
            value={selectedBranch ?? ""}
            onChange={(e) => handleBranchChange(e.target.value)}
            disabled={disabled}
            className={`flex-1 ${inputCls(branchError)}`}
          >
            <option value="">— brak / centrala —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {(b.care_labels?.length ? "⚠ " : "") + b.display_name}
              </option>
            ))}
          </select>
          {!disabled && (
            <button
              type="button"
              onClick={() => { setShowNewBranch((v) => !v); setShowNewSubBranch(false); }}
              className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Dodaj nowy oddział"
            >
              <Plus size={12} /> Nowy
            </button>
          )}
        </div>
        {branchError && <p className="text-xs text-red-600 mt-0.5">{branchError}</p>}
        {(() => {
          // Ostrzeżenie karty specyfiki widoczne już PRZY WYBORZE oddziału — prowadzący
          // ma wiedzieć, w co wchodzi, zanim przypnie projekt.
          const sel = branches.find((b) => b.id === selectedBranch);
          return sel?.care_labels?.length ? (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
              ⚠ Oddział pod szczególną opieką: {sel.care_labels.join(" · ")} — sprawdź kartę
              specyfiki w kartotece klienta.
            </p>
          ) : null;
        })()}

        {showNewBranch && (
          <div className="mt-2 flex gap-2 flex-wrap items-center">
            <input
              className={`flex-1 min-w-[140px] ${inputCls()}`}
              placeholder="Nazwa oddziału"
              value={newBranchName}
              onChange={(e) => setNewBranchName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddBranch()}
            />
            <input
              className={`w-28 ${inputCls()}`}
              placeholder="Miasto"
              value={newBranchCity}
              onChange={(e) => setNewBranchCity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddBranch()}
            />
            <button
              type="button"
              onClick={handleAddBranch}
              disabled={!newBranchName.trim() || createBranch.isPending}
              className="px-3 py-2 text-xs bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              Dodaj
            </button>
            <button
              type="button"
              onClick={() => setShowNewBranch(false)}
              className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              Anuluj
            </button>
          </div>
        )}
      </div>

      {/* Pododdział — widoczny tylko gdy wybrano oddział */}
      {selectedBranch && (
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Pododdział <span className="text-gray-400 text-xs">(opcjonalnie)</span>
          </label>
          <div className="flex gap-2">
            <select
              value={selectedSubBranch ?? ""}
              onChange={(e) => onSubBranchChange(e.target.value || null)}
              disabled={disabled}
              className={`flex-1 ${inputCls(subBranchError)}`}
            >
              <option value="">— brak —</option>
              {subBranches.map((sb) => (
                <option key={sb.id} value={sb.id}>
                  {(sb.care_labels?.length ? "⚠ " : "") + sb.display_name}
                </option>
              ))}
            </select>
            {!disabled && (
              <button
                type="button"
                onClick={() => setShowNewSubBranch((v) => !v)}
                className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                title="Dodaj nowy pododdział"
              >
                <Plus size={12} /> Nowy
              </button>
            )}
          </div>
          {subBranchError && <p className="text-xs text-red-600 mt-0.5">{subBranchError}</p>}
          {(() => {
            const sel = subBranches.find((sb) => sb.id === selectedSubBranch);
            return sel?.care_labels?.length ? (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                ⚠ Pododdział pod szczególną opieką: {sel.care_labels.join(" · ")} — sprawdź
                kartę specyfiki w kartotece klienta.
              </p>
            ) : null;
          })()}

          {showNewSubBranch && (
            <div className="mt-2 flex gap-2 flex-wrap items-center">
              <input
                className={`flex-1 min-w-[140px] ${inputCls()}`}
                placeholder="Nazwa pododdziału"
                value={newSubBranchName}
                onChange={(e) => setNewSubBranchName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddSubBranch()}
              />
              <input
                className={`w-28 ${inputCls()}`}
                placeholder="Miasto"
                value={newSubBranchCity}
                onChange={(e) => setNewSubBranchCity(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddSubBranch()}
              />
              <button
                type="button"
                onClick={handleAddSubBranch}
                disabled={!newSubBranchName.trim() || createSubBranch.isPending}
                className="px-3 py-2 text-xs bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                Dodaj
              </button>
              <button
                type="button"
                onClick={() => setShowNewSubBranch(false)}
                className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              >
                Anuluj
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
